import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

/**
 * Format for CSS box shadow
 * Reference: https://developer.mozilla.org/en-US/docs/Web/CSS/box-shadow
 * @example
 *   "boxShadow": {
 *      "isInset": null,
 *      "offsetX": "0px",
 *      "offsetY": "2px",
 *      "blur": "4px",
 *      "spread": "0px",
 *      "color": null
 *   }
 */
export interface UIBoxShadowConfig {
  isInset?: boolean;
  offsetX: string;
  offsetY: string;
  blur: string;
  spread: string;
  color?: string;
}

/**
 * Sets CSS box-shadow properties for a given HTML element based on configuration
 * @example
 *   <div [ui-box-shadow]="config.boxShadow"></div>
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[ui-box-shadow]',
})
export class UIBoxShadowDirective implements AfterViewInit, OnChanges {
  @Input('ui-box-shadow') config!: UIBoxShadowConfig;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.applyStyles();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.applyStyles();
    }
  }

  applyStyles() {
    const boxShadow = this.getBoxShadow(this.config);
    const el = this.elementRef.nativeElement;

    this.renderer.setStyle(el, 'box-shadow', boxShadow);
  }

  /**
   * Transforms the box shadow configuration to a CSS string
   * @example
   * // returns "0 2px 4px 0 var(--gray--border)"
   * this.getBoxShadow(this.config)
   * @returns {string} Returns the CSS box shadow string.
   */
  getBoxShadow(config: UIBoxShadowConfig): string {
    function bs(
      prop: 'isInset' | 'offsetX' | 'offsetY' | 'blur' | 'spread' | 'color',
      ctx = config,
    ) {
      return ctx[prop] || '0';
    }

    if (config) {
      return `${config.isInset ? 'inset' : ''} ${bs('offsetX')} ${bs(
        'offsetY',
      )} ${bs('blur')} ${bs('spread')} ${
        config.color || 'var(--gray--shadow)'
      }`;
    }

    return '';
  }
}
